import { createSelector } from '@reduxjs/toolkit';
import { cloneDeep } from 'es-toolkit';

import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { getItemSummaryIsDeleted, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { fetchCategoryResults } from '@/redux/modules/search/search.api';
import { getCurrentSearch, getSearchUiState } from '@/redux/modules/search/search.selectors';
import {
    getCategoryWithGreatestResults,
    getSelectedCategoryFacetValue,
    getSelectedOptionFacetValues,
    getUpcomingAuctionsCount,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';
import FacetTypes from '@/types/search/enums/facetTypes';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { AugmentedSearchQuery, AugmentedSearchQueryOptions, SearchQuery } from '@/types/search/searchQuery';
import { isArchivedSearch } from '@/utils/queryParams';

import type { CategoryLandingPageRouteParams } from './landingPage.types';

import {
    LOAD_CATEGORY_RESULTS_FAIL,
    LOAD_CATEGORY_RESULTS_FAIL_ACTION,
    LOAD_CATEGORY_RESULTS_REQUEST,
    LOAD_CATEGORY_RESULTS_REQUEST_ACTION,
    LOAD_CATEGORY_RESULTS_SUCCESS,
    LOAD_CATEGORY_RESULTS_SUCCESS_ACTION,
} from './actions';
import { getIsBot } from './analytics';
import { getClientIpAddress } from './browser';
import { LOAD_COMBINATION_CATEGORY_SUCCESS } from './combinationCategory';
import { getDeployment } from './config';
import { pickLandingPageMetaDataFromSearchPayload } from './landingPageMetaData';
import { getUserExcludedFacets } from './search/exclusions/searchExclusions.selectors';
import {
    CategorySearchResult,
    CategorySearchResultPayload,
    FetchCategoryResultsParams,
    isErrorResponse,
    SAVE_LAST_SEARCH_FETCH_OPTIONS,
} from './search/search.types';
import { clearPromotedSearchResults, searchPromoted } from './searchPromoted';

type LandingPageMetaDataType = {
    canonicalURL?: string;
    pageLabel: string;
    pageMetaDescription: string;
    pageMetaKeywords: string;
    pageTitle: string;
    /** e.g. `noindex, follow` when below SEO threshold */
    robots?: string;
};

type SearchCategoryParams = {
    ids: number[];
    pageName?: string;
    searchQuery: SearchQuery;
    skipAggs?: boolean;
    useScoutCategories?: boolean;
};

export type LandingPageStateType = {
    metaData: LandingPageMetaDataType;
};

// reducer
export const defaultLandingPageSlice: LandingPageStateType = {
    metaData: {
        pageLabel: '',
        pageMetaDescription: '',
        pageMetaKeywords: '',
        pageTitle: '',
        robots: undefined,
    },
};

export default function reducer(state = defaultLandingPageSlice, action: any = {}): LandingPageStateType {
    switch (action.type) {
        case LOAD_COMBINATION_CATEGORY_SUCCESS:
            return {
                ...state,
                metaData: {
                    ...state.metaData,
                    // @ts-ignore
                    pageLabel: action.payload.pageTitle.length ? action.payload.pageTitle : state.metaData.pageLabel,
                    pageMetaDescription: action.payload.meta_description.length
                        ? action.payload.meta_description
                        : state.metaData.pageMetaDescription,
                    pageTitle: action.payload.meta_title ? action.payload.meta_title : state.metaData.pageTitle,
                },
            };
        case LOAD_CATEGORY_RESULTS_SUCCESS:
            let metaData = state.metaData;

            if (action.payload.landingPageMetaData) {
                const landingPage = action.payload.landingPageMetaData;
                metaData = {
                    // @ts-ignore
                    canonicalURL: landingPage ? landingPage.canonicalURL : '',
                    pageLabel: landingPage.label,
                    pageMetaDescription: landingPage.metaDescription,
                    pageMetaKeywords: landingPage.metaKeywords,
                    pageTitle: landingPage.pageTitle,
                    robots: landingPage.robots,
                };
            }

            return {
                ...state,
                metaData,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;
const landingPageSlice = createSelector(stateSelector, (state) => state.landingPage);

const metaDataSelector = createSelector(landingPageSlice, (state) => state.metaData);

const getPageMetaDescription = createSelector(metaDataSelector, (metaData) => metaData.pageMetaDescription);

const getPageMetaKeywords = createSelector(metaDataSelector, (metaData) => metaData.pageMetaKeywords);

export const getPageTitle = createSelector(metaDataSelector, (metaData) => metaData.pageTitle);

export const getCanonicalURL = createSelector(metaDataSelector, (metaData) => metaData.canonicalURL);

export const getPageLabel = createSelector(metaDataSelector, (metaData) => {
    return metaData.pageLabel;
});

export const getCategoryLandingPageUrl = (params: CategoryLandingPageRouteParams) => {
    return getLandingPageUrl(params, 'c');
};

export const getAuctionResultsLandingPageUrl = (params: CategoryLandingPageRouteParams) => {
    return getLandingPageUrl(params, 'price-guide');
};

const getLandingPageUrl = (params: CategoryLandingPageRouteParams, route: string) => {
    if (params.browseCategorySlug) {
        const splat = params['*'];
        const extra = splat && String(splat).length ? `/${String(splat)}` : '';
        return `/${route}/${params.browseCategorySlug}${extra}/`;
    }
    if (!params.categoryId || Number.isNaN(Number(params.categoryId))) {
        return `/${route}/`;
    }
    if (params.categoryId2 && params.categoryName2) {
        return `/${route}/${params.categoryName}/${params.categoryName2}/${params.categoryId}/${params.categoryId2}/`;
    }
    return `/${route}/${params.categoryName}/${params.categoryId}/`;
};

export const getLandingPageMetaData = (
    state: GlobalState,
    params: CategoryLandingPageRouteParams,
    pathname: any,
    queryParams: any
) => {
    const { totalFound } = getSearchUiState(state);
    const canonicalURL = getCanonicalURL(state);
    const pageMetaDescription = getPageMetaDescription(state);
    const pageMetaKeywords = getPageMetaKeywords(state);
    const archivedSearch = isArchivedSearch(queryParams, pathname);
    const pageTitle = getPageTitle(state);
    const pageUrl = archivedSearch ? getAuctionResultsLandingPageUrl(params) : getCategoryLandingPageUrl(params);
    const categoryWithGreatestResults = getCategoryWithGreatestResults(state);
    const totalUpcomingAuctions = getUpcomingAuctionsCount(state);
    const selectedCreator = getSelectedOptionFacetValues(state, FacetTypes.CREATOR_ID);
    const selectedMaterials = getSelectedOptionFacetValues(state, FacetTypes.MATERIALS_AND_TECHNIQUES_ID);
    const selectedStyles = getSelectedOptionFacetValues(state, FacetTypes.STYLE_PERIOD_ID);
    const selectedOrigin = getSelectedOptionFacetValues(state, FacetTypes.ORIGIN_ID);
    const selectedCategory = getSelectedCategoryFacetValue(state, FacetTypes.CATEGORY_ID);

    const landingPageMetaData = {
        canonicalURL,
        categoryWithGreatestResults,
        pageMetaDescription,
        pageMetaKeywords,
        pageTitle,
        pageUrl,
        robots: state.landingPage.metaData.robots,
        selectedCategory,
        selectedCreator,
        selectedMaterials,
        selectedOrigin,
        selectedStyles,
        totalFound,
        totalUpcomingAuctions,
    };

    return cloneDeep(landingPageMetaData);
};

/* ACTION CREATORS */
const categoryResultsFail = (): LOAD_CATEGORY_RESULTS_FAIL_ACTION => ({
    type: LOAD_CATEGORY_RESULTS_FAIL,
});

const categoryResultsRequest = (): LOAD_CATEGORY_RESULTS_REQUEST_ACTION => ({
    type: LOAD_CATEGORY_RESULTS_REQUEST,
});

const categoryResultsSuccess = (results: CategorySearchResultPayload): LOAD_CATEGORY_RESULTS_SUCCESS_ACTION => ({
    error: false,
    meta: { actionTime: Date.now() },
    payload: results,
    type: LOAD_CATEGORY_RESULTS_SUCCESS,
});

export const searchCategory =
    ({ ids, pageName, searchQuery, skipAggs, useScoutCategories }: SearchCategoryParams) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const deployment = getDeployment(state);
            const authToken = getAuthToken(state);
            const ipAddress = getClientIpAddress(state);
            const isBot = getIsBot(state);

            const currentSearch = getCurrentSearch(state);

            // Annotate facets which have user-defined search exclusions
            const searchExclusions = getUserExcludedFacets(state);
            const augmentedOptions: AugmentedSearchQueryOptions = Object.keys(searchExclusions).reduce(
                (options, facetWithExclusions) => ({
                    ...options,
                    [facetWithExclusions]: [
                        {
                            exclude: searchExclusions[facetWithExclusions] || [],
                            include: (searchQuery.options && searchQuery.options[facetWithExclusions]) || [],
                        },
                    ],
                }),
                {
                    ...searchQuery.options,
                    auctionHouse: [{ exclude: [], include: [] }],
                }
            );
            // Combine URL path categories with query param categories instead of overwriting
            if (ids?.length) {
                const pathCategories = ids.map((id) => `${id}`);
                searchQuery.categories = searchQuery.categories?.length
                    ? [...searchQuery.categories, ...pathCategories]
                    : pathCategories;
            }
            const augmentedSearchQuery: AugmentedSearchQuery = {
                ...(isBot ? { isBot } : {}),
                ...searchQuery,
                buyNow: Boolean(pageName) && !pageName?.includes('price-guide'),
                fullResults: true,
                options: augmentedOptions,
            };

            const searchForCacheCheck = JSON.stringify(augmentedSearchQuery);

            if (searchForCacheCheck === currentSearch) {
                return Promise.resolve();
            }

            dispatch(categoryResultsRequest());

            const fetchOptions: FetchCategoryResultsParams = {
                authToken,
                deployment,
                ids,
                ipAddress,
                searchQuery: augmentedSearchQuery,
                skipAggs,
                useScoutCategories,
            };

            // Save options so we can re-run without skipAggs later for on-demand facets.
            // `satisfies` preserves the literal `true` that SearchSlice.lastSearchFetchOptions
            // requires for the category-search discriminator.
            dispatch({
                payload: { ...fetchOptions, isCategorySearch: true } satisfies FetchCategoryResultsParams & {
                    isCategorySearch: true;
                },
                type: SAVE_LAST_SEARCH_FETCH_OPTIONS,
            });

            // Tuple shape: the first promise carries the typed search result we destructure below;
            // subsequent promises (e.g. searchPromoted dispatch) are side-effects whose payloads we don't read.
            const promises: [Promise<CategorySearchResult>, ...Promise<unknown>[]] = [
                fetchCategoryResults(fetchOptions),
            ];

            // Only fetch promoted items for Most Relevant sort
            if (searchQuery.sort === SearchSortAndDirection.MostRelevant) {
                promises.push(dispatch(searchPromoted({ ids, pageName, searchQuery, useScoutCategories })));
            } else {
                // Clear promoted search results when sort is not Most Relevant
                dispatch(clearPromotedSearchResults());
            }

            const [result] = await Promise.all(promises);

            if (isErrorResponse(result)) {
                dispatch(categoryResultsFail());
            } else {
                result.payload.currentQueryKey = searchForCacheCheck;
                if (Boolean(augmentedSearchQuery?.searchTerm) && typeof result.payload === 'object') {
                    result.payload.sort = [];
                }
                const landingPageMetaData = pickLandingPageMetaDataFromSearchPayload(
                    result.payload.landingPageMetadata
                );
                const basePayload = skipAggs ? { ...result.payload, soldItemFacets: null } : result.payload;
                const payload = landingPageMetaData ? { ...basePayload, landingPageMetaData } : basePayload;
                dispatch(categoryResultsSuccess(payload));
            }
        } catch {
            dispatch(categoryResultsFail());
        }
    };

export const getDeletedItemTitleIfNeeded = (state: GlobalState, query: any) => {
    let returnTitle = '';
    if (query.deleted && !isNaN(query.deleted)) {
        const itemId = query.deleted;
        const isDeleted = getItemSummaryIsDeleted(state, itemId);
        const title = getItemSummaryTitle(state, itemId) || 'The item you were seeking';
        if (isDeleted) {
            returnTitle = title;
        }
    }
    return returnTitle;
};

//TODO: rename all the categories to landing where appropriate
